import * as React from 'react';
import { InputLabel, InputMessage } from '../shared';
import { type DatePickerSize, sizeHeightClasses } from '../DatePicker/dateUtils';

/**
 * Size variants for time picker components (mirrors DatePicker).
 * - sm: 32px (compact)
 * - md: 40px (default)
 * - lg: 48px (comfortable)
 */
export type TimePickerSize = DatePickerSize;

/**
 * Regex matching a valid `HH:mm` (24h) string.
 */
const TIME_REGEX = /^\d{2}:\d{2}$/;

/**
 * Returns `value` if it is a valid `HH:mm` string, otherwise empty string.
 * Defensive helper: native `<input type="time">` should never emit garbage,
 * but a controlled `value` prop coming from outside might.
 */
function sanitizeTimeValue(value: string | null | undefined): string {
  if (!value) return '';
  return TIME_REGEX.test(value) ? value : '';
}

/**
 * TimePicker component props.
 */
export interface TimePickerProps {
  /**
   * The selected time value as `HH:mm` (24h) string, or null when empty.
   */
  value?: string | null;
  /**
   * Callback when the time changes. Receives `HH:mm` or `null` when cleared.
   */
  onChange?: (value: string | null) => void;
  /**
   * Label text displayed above the input.
   */
  label?: string;
  /**
   * Placeholder text when no time is selected.
   * @default '--:--'
   */
  placeholder?: string;
  /**
   * Helper text displayed below the input.
   */
  helperText?: string;
  /**
   * Whether the input is in an error state.
   */
  error?: boolean;
  /**
   * Error message displayed when error is true.
   */
  errorMessage?: string;
  /**
   * Whether the field is required.
   */
  required?: boolean;
  /**
   * Whether the field is disabled.
   */
  disabled?: boolean;
  /**
   * Size variant of the input.
   * @default 'md'
   */
  size?: TimePickerSize;
  /**
   * Minute step in seconds. Native `<input type="time">` `step` attribute.
   * @default 60
   */
  step?: number;
  /**
   * Custom ID for the input element.
   */
  id?: string;
  /**
   * Additional CSS classes applied to the input.
   */
  className?: string;
  /**
   * Name attribute for form submission.
   */
  name?: string;
  /**
   * Callback when the input receives focus.
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Callback when the input loses focus.
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * Base classes for the input element. Mirrors DatePicker's input chrome,
 * minus the `pr-10` right padding (the native time input draws its own
 * clock glyph; no overlay button is rendered).
 */
const inputBaseClasses = [
  'w-full',
  'rounded-[6px]',
  'border',
  'px-3',
  'text-[15px]',
  'leading-[22px]',
  'outline-none',
  'transition-all',
  'duration-150',
  'ease-out',
  'placeholder:text-secondary-400',
].join(' ');

/**
 * Get input classes based on error and disabled state. Mirrors DatePicker.
 */
function getInputStateClasses(error: boolean, disabled: boolean): string {
  if (disabled) {
    return 'bg-secondary-100 text-secondary-400 border-secondary-300 cursor-not-allowed';
  }
  if (error) {
    return 'border-error-700 focus:ring-2 focus:ring-error-700/20';
  }
  return 'border-secondary-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-200';
}

/**
 * TimePicker component for selecting a time-of-day in `HH:mm` (24h) format.
 *
 * Features:
 * - Native `<input type="time">` under the hood — browser supplies the clock
 *   affordance and OS-native time selection UI for free.
 * - Visual chrome (border, height, focus ring, error state) matches DatePicker.
 * - Controlled `HH:mm` string value; emits `null` when cleared.
 * - Accessible with proper ARIA attributes.
 *
 * @example
 * // Basic usage
 * <TimePicker label="Time" onChange={(time) => console.log(time)} />
 *
 * @example
 * // With 5-minute step
 * <TimePicker label="Reminder" step={300} value={value} onChange={setValue} />
 */
const TimePicker = React.forwardRef<HTMLInputElement, TimePickerProps>(
  (
    {
      value,
      onChange,
      label,
      placeholder = '--:--',
      helperText,
      error = false,
      errorMessage,
      required = false,
      disabled = false,
      size = 'md',
      step = 60,
      id: providedId,
      className = '',
      name,
      onFocus,
      onBlur,
    },
    ref
  ) => {
    // Generate stable IDs for accessibility
    const reactId = React.useId();
    const inputId = providedId || `timepicker-${reactId}`;
    const helperId = `timepicker-helper-${reactId}`;

    // Internal input value mirrors the prop, but stays editable so the user
    // can type without the parent re-render fighting them. Falls back to the
    // empty string if the prop is null/undefined or malformed.
    const [inputValue, setInputValue] = React.useState(() => sanitizeTimeValue(value));

    // Sync prop -> state when controlled value changes externally.
    React.useEffect(() => {
      setInputValue(sanitizeTimeValue(value));
    }, [value]);

    // Handlers
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      if (newValue === '') {
        onChange?.(null);
        return;
      }

      // Native `<input type="time">` only emits valid HH:mm or empty.
      // Still, guard defensively in case a userland change event provides
      // something else.
      if (TIME_REGEX.test(newValue)) {
        onChange?.(newValue);
      }
    };

    // Determine which message to display
    const showError = error && errorMessage;
    const messageId = showError || helperText ? helperId : undefined;

    // Input classes - computed using helper functions
    const inputClasses = [
      sizeHeightClasses[size],
      inputBaseClasses,
      getInputStateClasses(error, disabled),
      className,
    ].join(' ');

    return (
      <div className="relative w-full">
        {/* Label */}
        {label && <InputLabel label={label} htmlFor={inputId} required={required} />}

        {/* Input container */}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type="time"
            name={name}
            value={inputValue}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            step={step}
            aria-invalid={error}
            aria-required={required}
            aria-describedby={messageId}
            className={inputClasses}
          />
        </div>

        {/* Helper text or error message */}
        <InputMessage
          error={error}
          errorMessage={errorMessage}
          helperText={helperText}
          helperId={helperId}
        />
      </div>
    );
  }
);

TimePicker.displayName = 'TimePicker';

export { TimePicker };
